import { useState } from 'react'
import { Head, Link, useForm, router } from '@inertiajs/react'
import { ArrowLeft, ThumbsUp, MessageCircle, CheckCircle, HelpCircle, MessageSquare, Megaphone, Calendar, Pencil, Trash2, Send, Eye, X, BellRing, BellOff } from 'lucide-react'
import { MainLayout } from '@/components/layouts'
import { MentionTextarea } from '@/components/ui/MentionTextarea'
import { AttachmentPicker, AttachmentList } from '@/components/ui/AttachmentPicker'
import type { PostDetail, Comment, PostAuthor, PostCategory } from '@/types'

interface ShowProps {
  post: PostDetail
  comments: Comment[]
  categories: PostCategory[]
  is_subscribed: boolean
}

const POST_TYPE_CONFIG = {
  question: {
    icon: HelpCircle,
    label: 'Question',
    className: 'bg-blue-100 text-blue-800'
  },
  discussion: {
    icon: MessageSquare,
    label: 'Discussion',
    className: 'bg-gray-100 text-gray-800'
  },
  announcement: {
    icon: Megaphone,
    label: 'Annonce',
    className: 'bg-tessan-cream text-tessan-primary'
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return "à l'instant"
  if (diffInSeconds < 3600) return `il y a ${Math.floor(diffInSeconds / 60)} min`
  if (diffInSeconds < 86400) return `il y a ${Math.floor(diffInSeconds / 3600)} h`
  if (diffInSeconds < 604800) return `il y a ${Math.floor(diffInSeconds / 86400)} j`

  return formatDate(dateString)
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function AuthorAvatar({ author, size = 'md' }: { author: PostAuthor; size?: 'sm' | 'md' }) {
  const sizeClasses = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm'
  return (
    <div className={`flex-shrink-0 ${sizeClasses} rounded-full bg-tessan-primary text-white flex items-center justify-center font-medium`}>
      {getInitials(author.name)}
    </div>
  )
}

interface CommentCardProps {
  comment: Comment
  postId: number
}

function CommentCard({ comment, postId }: CommentCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editBody, setEditBody] = useState(comment.body)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleUpdate = () => {
    router.patch(`/posts/${postId}/comments/${comment.id}`, {
      comment: { body: editBody }
    }, {
      onSuccess: () => setIsEditing(false)
    })
  }

  const handleDelete = () => {
    router.delete(`/posts/${postId}/comments/${comment.id}`)
  }

  const handleLike = () => {
    if (comment.is_liked) {
      router.delete(`/posts/${postId}/comments/${comment.id}/like`, { preserveScroll: true })
    } else {
      router.post(`/posts/${postId}/comments/${comment.id}/like`, {}, { preserveScroll: true })
    }
  }

  return (
    <div className={`flex gap-3 p-4 rounded-lg ${comment.is_solution ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
      <AuthorAvatar author={comment.author} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900">{comment.author.name}</span>
            <span className="text-gray-400">·</span>
            <span className="text-sm text-gray-500">{comment.author.specialty}</span>
            <span className="text-gray-400">·</span>
            <time className="text-sm text-gray-500" dateTime={comment.created_at}>
              {formatRelativeDate(comment.created_at)}
            </time>
            {comment.is_solution && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3" />
                Solution
              </span>
            )}
          </div>

          {/* Edit/Delete buttons for author */}
          {comment.is_author && !isEditing && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 text-gray-400 hover:text-tessan-primary transition-colors"
                title="Modifier"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsDeleting(true)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-3">
            <textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tessan-primary focus:border-transparent resize-none"
              rows={3}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={handleUpdate}
                className="px-3 py-1.5 bg-tessan-primary text-white text-sm rounded-lg hover:bg-tessan-primary-hover transition-colors"
              >
                Enregistrer
              </button>
              <button
                onClick={() => {
                  setIsEditing(false)
                  setEditBody(comment.body)
                }}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-gray-700 whitespace-pre-wrap">{comment.body}</p>
            {comment.attachments && comment.attachments.length > 0 && (
              <div className="mt-2">
                <AttachmentList attachments={comment.attachments} />
              </div>
            )}
          </>
        )}

        {/* Delete confirmation */}
        {isDeleting && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800 mb-2">Supprimer ce commentaire ?</p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDelete}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
              <button
                onClick={() => setIsDeleting(false)}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {!isEditing && !isDeleting && (
          <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
            <button
              onClick={handleLike}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all ${
                comment.is_liked
                  ? 'text-tessan-primary bg-tessan-primary/10'
                  : 'hover:text-tessan-primary hover:bg-gray-100'
              }`}
            >
              <ThumbsUp className={`w-4 h-4 ${comment.is_liked ? 'fill-tessan-primary' : ''}`} />
              {comment.likes_count}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

interface EditPostModalProps {
  post: PostDetail
  categories: PostCategory[]
  onClose: () => void
}

function EditPostModal({ post, categories, onClose }: EditPostModalProps) {
  const { data, setData, post: submitForm, processing, errors, transform } = useForm<{
    _method: string
    title: string
    body: string
    post_type: 'question' | 'discussion' | 'announcement'
    category_id: number | string
    attachments: File[]
    delete_attachment_ids: number[]
  }>({
    _method: 'patch',
    title: post.title,
    body: post.body,
    post_type: post.post_type,
    category_id: post.category?.id || '',
    attachments: [],
    delete_attachment_ids: []
  })

  transform(d => ({
    _method: d._method,
    post: {
      title: d.title,
      body: d.body,
      post_type: d.post_type,
      category_id: d.category_id,
      attachments: d.attachments,
      delete_attachment_ids: d.delete_attachment_ids
    }
  }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    submitForm(`/posts/${post.id}`, {
      forceFormData: data.attachments.length > 0,
      onSuccess: () => onClose()
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Modifier la publication</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Post type selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type de publication</label>
            <div className="flex gap-2">
              {(['question', 'discussion', 'announcement'] as const).map((type) => {
                const config = POST_TYPE_CONFIG[type]
                const Icon = config.icon
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setData('post_type', type)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                      data.post_type === type
                        ? 'border-tessan-primary bg-tessan-primary/10 text-tessan-primary'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {config.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700 mb-1">
              Titre
            </label>
            <input
              id="edit-title"
              type="text"
              value={data.title}
              onChange={(e) => setData('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tessan-primary focus:border-transparent"
              required
              minLength={5}
              maxLength={200}
            />
            {errors.title && <p className="text-sm text-red-600 mt-1">{errors.title}</p>}
          </div>

          {/* Body */}
          <div>
            <label htmlFor="edit-body" className="block text-sm font-medium text-gray-700 mb-1">
              Contenu
            </label>
            <textarea
              id="edit-body"
              value={data.body}
              onChange={(e) => setData('body', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tessan-primary focus:border-transparent resize-none"
              rows={6}
              required
              minLength={10}
            />
            {errors.body && <p className="text-sm text-red-600 mt-1">{errors.body}</p>}
          </div>

          {/* Category */}
          <div>
            <label htmlFor="edit-category" className="block text-sm font-medium text-gray-700 mb-1">
              Categorie (optionnel)
            </label>
            <select
              id="edit-category"
              value={data.category_id}
              onChange={(e) => setData('category_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tessan-primary focus:border-transparent"
            >
              <option value="">Aucune categorie</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pièces jointes
            </label>
            <AttachmentPicker
              files={data.attachments}
              onFilesChange={(files) => setData('attachments', files)}
              existing={post.attachments || []}
              deletedIds={data.delete_attachment_ids}
              onDeletedIdsChange={(ids) => setData('delete_attachment_ids', ids)}
            />
            {errors.attachments && (
              <p className="text-sm text-red-600 mt-1">{errors.attachments}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={processing}
              className="px-4 py-2 bg-tessan-primary text-white rounded-lg hover:bg-tessan-primary-hover transition-colors disabled:opacity-50"
            >
              {processing ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function CommentForm({ postId }: { postId: number }) {
  const { data, setData, post, processing, reset, errors, transform } = useForm<{
    body: string
    attachments: File[]
  }>({
    body: '',
    attachments: []
  })

  transform(d => ({
    comment: { body: d.body, attachments: d.attachments }
  }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/posts/${postId}/comments`, {
      forceFormData: data.attachments.length > 0,
      onSuccess: () => reset()
    })
  }

  return (
    <form onSubmit={handleSubmit} className="mt-6 pt-6 border-t border-gray-200">
      <label htmlFor="comment-body" className="block text-sm font-medium text-gray-700 mb-2">
        Ajouter un commentaire
      </label>
      <div className="space-y-3">
        <MentionTextarea
          id="comment-body"
          value={data.body}
          onChange={(val) => setData('body', val)}
          placeholder="Votre commentaire... (tapez @ pour mentionner)"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tessan-primary focus:border-transparent resize-none"
          rows={3}
        />
        {errors.body && (
          <p className="text-sm text-red-600">{errors.body}</p>
        )}
        <AttachmentPicker
          files={data.attachments}
          onFilesChange={(files) => setData('attachments', files)}
        />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={processing || !data.body.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-tessan-primary text-white rounded-lg hover:bg-tessan-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            Envoyer
          </button>
        </div>
      </div>
    </form>
  )
}

export default function Show({ post, comments, categories, is_subscribed }: ShowProps) {
  const typeConfig = POST_TYPE_CONFIG[post.post_type]
  const TypeIcon = typeConfig.icon
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handlePostLike = () => {
    if (post.is_liked) {
      router.delete(`/posts/${post.id}/like`, { preserveScroll: true })
    } else {
      router.post(`/posts/${post.id}/like`, {}, { preserveScroll: true })
    }
  }

  const handleDelete = () => {
    router.delete(`/posts/${post.id}`)
  }

  return (
    <MainLayout title={post.title}>
      <Head title={`${post.title} - Communaute`} />

      {/* Edit Modal */}
      {isEditing && (
        <EditPostModal
          post={post}
          categories={categories}
          onClose={() => setIsEditing(false)}
        />
      )}

      <div>
        {/* Back link */}
        <Link
          href={post.interpro ? '/medecins-pharmaciens' : '/community'}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-tessan-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {post.interpro ? 'Retour à la Passerelle Santé' : 'Retour a la communaute'}
        </Link>

        {/* Post */}
        <article className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <AuthorAvatar author={post.author} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium text-gray-900">{post.author.name}</span>
                    <span className="text-gray-400">·</span>
                    <span className="text-sm text-gray-500">{post.author.specialty}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4" />
                    <time dateTime={post.created_at}>{formatDate(post.created_at)}</time>
                    {post.updated_at && (
                      <>
                        <span className="text-gray-400">·</span>
                        <span className="text-gray-400 italic">
                          Modifie le {formatDate(post.updated_at)}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Edit/Delete buttons */}
                {(post.can_edit || post.can_delete) && (
                  <div className="flex items-center gap-1">
                    {post.can_edit && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="p-2 text-gray-400 hover:text-tessan-primary transition-colors rounded-lg hover:bg-gray-100"
                        title="Modifier"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {post.can_delete && (
                      <button
                        onClick={() => setIsDeleting(true)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-gray-100"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Delete confirmation */}
          {isDeleting && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 mb-3">
                Etes-vous sur de vouloir supprimer cette publication ? Cette action est irreversible.
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                >
                  Supprimer
                </button>
                <button
                  onClick={() => setIsDeleting(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {/* Badges */}
          <div className="flex items-center gap-2 mb-4">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${typeConfig.className}`}>
              <TypeIcon className="w-4 h-4" />
              {typeConfig.label}
            </span>
            {post.category && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                {post.category.name}
              </span>
            )}
            {post.has_solution && post.post_type === 'question' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <CheckCircle className="w-4 h-4" />
                Resolu
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            {post.title}
          </h1>

          {/* Body */}
          <div className="prose prose-gray max-w-none mb-6">
            <p className="text-gray-700 whitespace-pre-wrap">{post.body}</p>
          </div>

          {/* Attachments */}
          {post.attachments && post.attachments.length > 0 && (
            <div className="mb-6">
              <AttachmentList attachments={post.attachments} />
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center gap-4 pt-4 border-t border-gray-200 text-sm text-gray-500">
            <button
              onClick={handlePostLike}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
                post.is_liked
                  ? 'text-tessan-primary bg-tessan-primary/10'
                  : 'hover:text-tessan-primary hover:bg-gray-100'
              }`}
            >
              <ThumbsUp className={`w-4 h-4 ${post.is_liked ? 'fill-tessan-primary' : ''}`} />
              {post.likes_count} {post.likes_count === 1 ? 'like' : 'likes'}
            </button>
            <span className="inline-flex items-center gap-1">
              <MessageCircle className="w-4 h-4" />
              {post.comments_count} {post.comments_count === 1 ? 'commentaire' : 'commentaires'}
            </span>
            <span className="inline-flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {post.views_count} {post.views_count === 1 ? 'vue' : 'vues'}
            </span>

            <button
              onClick={() => {
                if (is_subscribed) {
                  router.delete('/subscriptions', {
                    data: { subscribable_type: 'Post', subscribable_id: post.id },
                    preserveScroll: true
                  })
                } else {
                  router.post('/subscriptions', {
                    subscribable_type: 'Post', subscribable_id: post.id
                  }, { preserveScroll: true })
                }
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ml-auto ${
                is_subscribed
                  ? 'text-tessan-primary bg-tessan-primary/10'
                  : 'hover:text-tessan-primary hover:bg-gray-100'
              }`}
            >
              {is_subscribed ? <BellOff className="w-4 h-4" /> : <BellRing className="w-4 h-4" />}
              {is_subscribed ? 'Ne plus suivre' : 'Suivre'}
            </button>
          </div>
        </article>

        {/* Comments section */}
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="font-heading text-xl font-semibold text-gray-900 mb-6">
            Commentaires ({comments.length})
          </h2>

          {comments.length > 0 ? (
            <div className="space-y-4">
              {comments.map((comment) => (
                <CommentCard key={comment.id} comment={comment} postId={post.id} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aucun commentaire pour le moment.</p>
              <p className="text-sm">Soyez le premier a repondre !</p>
            </div>
          )}

          {/* Add comment form */}
          <CommentForm postId={post.id} />
        </section>
      </div>
    </MainLayout>
  )
}
